import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { PickpointStation, PickpointStationShort, PickpointShipment } from '../_models/shipping-config.interface';

@Injectable({ providedIn: 'root' })
export class PickpointStationService {
  private readonly basePath = environment.apiURL + 'pickpoint_stations';

  constructor(private http: HttpClient) {}

  getStations(activeOnly = false): Observable<PickpointStation[]> {
    const params: Record<string, string> = {};
    if (activeOnly) params['active_only'] = 'true';
    return this.http.get<PickpointStation[]>(`${this.basePath}.json`, { params, withCredentials: true });
  }

  getStation(id: number): Observable<{ station: PickpointStation; shipments: PickpointShipment[] }> {
    return this.http.get<{ station: PickpointStation; shipments: PickpointShipment[] }>(
      `${this.basePath}/${id}.json`, { withCredentials: true }
    );
  }

  searchStations(query: string): Observable<PickpointStationShort[]> {
    return this.http.get<PickpointStationShort[]>(
      `${this.basePath}/search.json`, { params: { q: query }, withCredentials: true }
    );
  }

  getNearbyStations(lat: number, lng: number, radiusKm: number, date?: string): Observable<PickpointStation[]> {
    const params: Record<string, string> = {
      lat: String(lat),
      lng: String(lng),
      radius_km: String(radiusKm),
    };
    if (date) params['date'] = date;
    return this.http.get<PickpointStation[]>(
      `${this.basePath}/nearby.json`, { params, withCredentials: true }
    );
  }

  /**
   * Pro Operation die naheste PickPoint-Station (Luftlinie) liefern.
   * Wenn eine Operation keine Koordinaten hat, ist `station` = null.
   */
  async nearestForOperations(
    operationIds: number[],
    date?: string,
  ): Promise<{ operation_id: number; station: PickpointStation | null; distance_km: number | null }[]> {
    const body: Record<string, unknown> = { operation_ids: operationIds };
    if (date) body['date'] = date;
    const result = await firstValueFrom(
      this.http.post<{ results: { operation_id: number; station: PickpointStation | null; distance_km: number | null }[] }>(
        `${this.basePath}/nearest_for_operations.json`,
        body,
        { withCredentials: true },
      ),
    );
    return result.results;
  }

  createStation(data: Partial<PickpointStation>): Observable<PickpointStation> {
    return this.http.post<PickpointStation>(`${this.basePath}.json`, data, { withCredentials: true });
  }

  updateStation(id: number, data: Partial<PickpointStation>): Observable<PickpointStation> {
    return this.http.patch<PickpointStation>(`${this.basePath}/${id}.json`, data, { withCredentials: true });
  }

  toggleActive(id: number, active: boolean): Observable<PickpointStation> {
    return this.http.patch<PickpointStation>(`${this.basePath}/${id}.json`, { active }, { withCredentials: true });
  }

  async importStations(stations: Partial<PickpointStation>[]): Promise<{
    success: boolean; imported: number; created: number; updated: number; errors: unknown[];
  }> {
    return firstValueFrom(
      this.http.post<{
        success: boolean; imported: number; created: number; updated: number; errors: unknown[];
      }>(`${this.basePath}/import.json`, { stations }, { withCredentials: true })
    );
  }

  destroyStation(id: number): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(`${this.basePath}/${id}.json`, { withCredentials: true });
  }
}
